/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import { getCarriers, updatePackageStatus, getParcelBoxes, updateBoxStatus, getTrackingMessages } from "network/service";

const status_titles = {
    0: "Pending",
    5: "Picked Up",
    1: "Intransit",
    2: "On Hold",
    3: "Delivered",
    4: "Issue",
};

const statusColors = {
    0: "#9e9e9e",
    5: "#2196f3",
    1: "#ff9800",
    2: "#f44336",
    3: "#4caf50",
    4: "#f44336",
};

function StatusDialog(props) {
    const [notiDetails, setShowDetails] = useState({ show: false });
    const [updateTarget, setUpdateTarget] = useState("package"); // "package" | "box"
    const [status, setStatus] = useState(props.details.status);
    const [carriers, setCarriers] = useState([]);
    const [carrier, setCarrier] = useState(null);
    const [loading, setLoading] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState("");
    const [message, setMessage] = useState("");
    const [trackingMessages, setTrackingMessages] = useState([]);
    const [boxes, setBoxes] = useState([]);
    const [selectedBox, setSelectedBox] = useState(null);
    const [loadingBoxes, setLoadingBoxes] = useState(false);
    const [allBoxesSameBanner, setAllBoxesSameBanner] = useState(null); // null | { status, title }

    const closeSB = () => setShowDetails((prev) => ({ ...prev, show: false }));
    const resetTrackingInputs = () => {
        setTrackingNumber("");
        setMessage("");
    };

    useEffect(() => {
        getCarriers()
            .then((resp) => setCarriers(resp.data))
            .catch(console.error);
    }, []);

    useEffect(() => {
        getTrackingMessages()
            .then((resp) => {
                const allTemplates = resp.data || [];
                setTrackingMessages(allTemplates.filter((item) => item.is_active !== false));
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (props.isopen && props.details) {
            setCarrier(props.details.carrier);
            setStatus(props.details.status);
            setUpdateTarget("package");
            setBoxes([]);
            setSelectedBox(null);
            setAllBoxesSameBanner(null);
            resetTrackingInputs();
            // Start from the shipment's current number (e.g. the UPS number from a vendor booking).
            setTrackingNumber(props.details.tracking_number || "");
        }
    }, [props.isopen, props.details]);

    // Load boxes when switching to individual-box mode
    useEffect(() => {
        if (updateTarget !== "box") return;
        if (boxes.length > 0) return; // already loaded
        setLoadingBoxes(true);
        getParcelBoxes(props.details._id)
            .then((resp) => {
                const normalizedBoxes = (resp.data || []).map((box, index) => ({
                    ...box,
                    display_number: index + 1,
                }));
                setBoxes(normalizedBoxes);
                if (normalizedBoxes.length > 0) {
                    const first = normalizedBoxes[0];
                    setSelectedBox(first);
                    setStatus(first.status !== undefined ? first.status : 0);
                    setTrackingNumber(first.tracking_number || "");
                }
                setLoadingBoxes(false);
            })
            .catch((err) => {
                console.error(err);
                setLoadingBoxes(false);
            });
    }, [updateTarget]);

    const handleBoxSelect = (box) => {
        setSelectedBox(box);
        setStatus(box.status !== undefined ? box.status : 0);
        setTrackingNumber(box.tracking_number || "");
        setMessage("");
        setAllBoxesSameBanner(null);
    };

    const handleSave = () => {
        if (loading) return;
        setLoading(true);
        setAllBoxesSameBanner(null);

        if (updateTarget === "box" && selectedBox) {
            updateBoxStatus({
                parcel_id: props.details._id,
                box_id: selectedBox._id,
                box_number: selectedBox.display_number || selectedBox.number,
                status: parseInt(status),
                tracking_number: trackingNumber || "",
                message: message,
            })
                .then((resp) => {
                    setLoading(false);
                    // Reflect updated status locally so the dropdown shows the new value
                    setBoxes((prev) =>
                        prev.map((b) =>
                            b._id === selectedBox._id
                                ? { ...b, status: parseInt(status), tracking_number: trackingNumber || b.tracking_number }
                                : b
                        )
                    );
                    setSelectedBox((prev) => ({ ...prev, status: parseInt(status) }));

                    if (resp.data.all_boxes_same_status) {
                        setAllBoxesSameBanner({
                            status: resp.data.suggested_parcel_status,
                            title: resp.data.same_status_title,
                        });
                    } else {
                        setShowDetails({
                            color: "success",
                            icon: "check",
                            title: "Updated",
                            message: `Box ${selectedBox.display_number || selectedBox.number} status set to ${status_titles[parseInt(status)]}`,
                            show: true,
                        });
                    }
                    resetTrackingInputs();
                })
                .catch(() => {
                    setLoading(false);
                    setShowDetails({
                        color: "error",
                        icon: "error",
                        title: "Failed",
                        message: "Could not update box status",
                        show: true,
                    });
                });
        } else {
            updatePackageStatus(props.details._id, {
                status: parseInt(status),
                tracking_number: trackingNumber || undefined,
                message: message,
            })
                .then(() => {
                    setLoading(false);
                    resetTrackingInputs();
                    props.apply();
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    };

    const handleUpdatePackageToo = () => {
        if (!allBoxesSameBanner) return;
        setLoading(true);
        updatePackageStatus(props.details._id, {
            status: allBoxesSameBanner.status,
            message: `All boxes are now ${allBoxesSameBanner.title}`,
        })
            .then(() => {
                setLoading(false);
                setAllBoxesSameBanner(null);
                resetTrackingInputs();
                props.apply();
            })
            .catch(() => setLoading(false));
    };

    const renderSB = (
        <MDSnackbar
            color={notiDetails.color}
            icon={notiDetails.icon}
            title={notiDetails.title}
            content={notiDetails.message}
            dateTime=""
            open={notiDetails.show}
            onClose={closeSB}
            close={closeSB}
            bgWhite
        />
    );

    return (
        <Dialog open={props.isopen} maxWidth="sm" fullWidth onClose={props.handleclose}>
            <DialogContent>
                <FormGroup>
                    <MDBox component="form" noValidate width="100%">
                        <MDTypography variant="h5" fontWeight="bold" color="dark" mb={2}>
                            Update Status
                        </MDTypography>

                        {/* ── Update Target Toggle ── */}
                        <MDBox mb={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                Update For
                            </MDTypography>
                            <MDBox mt={0.75}>
                                <ToggleButtonGroup
                                    value={updateTarget}
                                    exclusive
                                    size="small"
                                    onChange={(_, val) => {
                                        if (val) {
                                            setUpdateTarget(val);
                                            setAllBoxesSameBanner(null);
                                        }
                                    }}
                                >
                                    <ToggleButton value="package">Full Package</ToggleButton>
                                    <ToggleButton value="box">
                                        Individual Box
                                        {props.details.boxes && props.details.boxes.length > 1 && (
                                            <Chip
                                                label={props.details.boxes.length}
                                                size="small"
                                                color="primary"
                                                sx={{ ml: 0.75, height: 18, fontSize: "0.6rem" }}
                                            />
                                        )}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </MDBox>
                        </MDBox>

                        {/* ── Box Selector ── */}
                        {updateTarget === "box" && (
                            <MDBox mb={2}>
                                {loadingBoxes ? (
                                    <MDBox display="flex" alignItems="center" sx={{ gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <MDTypography variant="caption">Loading boxes…</MDTypography>
                                    </MDBox>
                                ) : (
                                    <FormControl fullWidth>
                                        <InputLabel>Select Box</InputLabel>
                                        <Select
                                            value={selectedBox?._id || ""}
                                            label="Select Box"
                                            onChange={(e) => {
                                                const box = boxes.find((b) => b._id === e.target.value);
                                                if (box) handleBoxSelect(box);
                                            }}
                                            style={{ padding: "10px 14px" }}
                                        >
                                            {boxes.map((box) => (
                                                <MenuItem key={box._id} value={box._id}>
                                                    <MDBox display="flex" alignItems="center" sx={{ gap: 1 }}>
                                                        <span>Box {box.display_number || box.number}</span>
                                                        <Chip
                                                            label={status_titles[box.status !== undefined ? box.status : 0]}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: statusColors[box.status !== undefined ? box.status : 0],
                                                                color: "white",
                                                                fontSize: "0.65rem",
                                                                height: 20,
                                                            }}
                                                        />
                                                    </MDBox>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </MDBox>
                        )}

                        {/* ── Carrier & Status ── */}
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={6}>
                                <Autocomplete
                                    disableClearable
                                    disabled
                                    options={carriers}
                                    getOptionLabel={(option) => option.name || ""}
                                    renderInput={(params) => <MDInput {...params} label="Carrier" />}
                                    value={carrier}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ width: "100%" }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        style={{ padding: "10px 14px" }}
                                        onChange={(e) => setStatus(e.target.value)}
                                        label="Status"
                                        value={status}
                                    >
                                        {Object.keys(status_titles).map((key) => (
                                            <MenuItem key={key} value={key}>
                                                {status_titles[key]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* ── Tracking Number ── */}
                        <MDBox mb={2}>
                            <MDInput
                                label={updateTarget === "box" ? "Box Tracking Number (AWB)" : "Tracking Number"}
                                fullWidth
                                style={{ width: "100%" }}
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                            />
                        </MDBox>

                        {/* ── Message ── */}
                        <MDBox mb={2}>
                            <Autocomplete
                                freeSolo
                                options={trackingMessages}
                                getOptionLabel={(option) => {
                                    if (typeof option === "string") return option;
                                    return option.content || "";
                                }}
                                inputValue={message}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === "input" || reason === "clear") {
                                        setMessage(newInputValue);
                                    }
                                }}
                                onChange={(event, newValue) => {
                                    if (typeof newValue === "string") {
                                        setMessage(newValue);
                                    } else if (newValue && newValue.content) {
                                        setMessage(newValue.content);
                                    }
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <MDBox display="flex" flexDirection="column">
                                            <MDTypography variant="button">{option.content}</MDTypography>
                                            <MDTypography variant="caption" color="text">{option.name}</MDTypography>
                                        </MDBox>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <MDInput
                                        {...params}
                                        label="Message"
                                        multiline
                                        rows={4}
                                        fullWidth
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </MDBox>

                        {/* ── All-boxes-same-status suggestion banner ── */}
                        {allBoxesSameBanner && (
                            <Alert
                                severity="success"
                                sx={{ mb: 2 }}
                                action={
                                    <Button size="small" color="inherit" onClick={handleUpdatePackageToo} disabled={loading}>
                                        Yes, update
                                    </Button>
                                }
                            >
                                All boxes are now <strong>{allBoxesSameBanner.title}</strong>. Update the overall package status too?
                            </Alert>
                        )}

                        {/* ── Save Button ── */}
                        <MDBox display="flex" justifyContent="flex-end">
                            <Box sx={{ position: "relative" }}>
                                <Button
                                    variant="contained"
                                    style={{ backgroundColor: "#61d261", color: "black", minWidth: 80 }}
                                    disabled={loading || (updateTarget === "box" && !selectedBox)}
                                    onClick={handleSave}
                                >
                                    Save
                                </Button>
                                {loading && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </MDBox>

                        {renderSB}
                    </MDBox>
                </FormGroup>
            </DialogContent>
        </Dialog>
    );
}

export default StatusDialog;
